import { For, Show, createEffect, createSignal } from "solid-js";
import { ApiError, api } from "../api/client";
import type { CatalogExercise } from "../api/types";
import { t, type MessageKey } from "../i18n";
import { EXERCISE_TYPES, normalizeType, type ExerciseType } from "../lib/activity";
import { PICKABLE_ICONS } from "../lib/exerciseIcons";
import { tgConfirm } from "../lib/telegram";
import BottomSheet from "./BottomSheet";
import ExerciseIcon from "./ExerciseIcon";
import ExerciseProgress from "./ExerciseProgress";
import ExerciseRecords from "./ExerciseRecords";
import MuscleGroupPicker from "./MuscleGroupPicker";

const TYPE_SUB: Record<ExerciseType, MessageKey> = {
  strength: "TYPE_STRENGTH_SUB",
  timed: "TYPE_TIMED_SUB",
  distance: "TYPE_DISTANCE_SUB",
  session: "TYPE_SESSION_SUB",
};
const TYPE_TITLE: Record<ExerciseType, MessageKey> = {
  strength: "TYPE_STRENGTH",
  timed: "TYPE_TIMED",
  distance: "TYPE_DISTANCE",
  session: "TYPE_SESSION",
};

interface Props {
  open: boolean;
  /** Catalog entry being edited; null/undefined = create. */
  exercise?: CatalogExercise | null;
  /** Name to start with when creating. */
  initialName?: string;
  onClose: () => void;
  onSaved: (item: CatalogExercise) => void;
  onDeleted?: (id: string) => void;
}

/** Create / edit a catalog exercise: name, type, icon, muscle groups. */
export default function CatalogExerciseSheet(props: Props) {
  const [name, setName] = createSignal("");
  const [type, setType] = createSignal<ExerciseType>("strength");
  const [icon, setIcon] = createSignal<string | null>(null);
  const [groups, setGroups] = createSignal<string[]>([]);
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  /** The server refused a type change (409) for an exercise we thought was free. */
  const [serverLocked, setServerLocked] = createSignal(false);

  createEffect(() => {
    if (!props.open) return;
    const ex = props.exercise;
    setName(ex?.name ?? props.initialName ?? "");
    setType(ex ? normalizeType(ex.exerciseType) : "strength");
    setIcon(ex?.icon ?? null);
    setGroups([...(ex?.muscleGroups ?? [])]);
    setError(null);
    setSaving(false);
    setServerLocked(false);
  });

  const editing = () => !!props.exercise;
  const locked = () => editing() && (props.exercise?.hasHistory === true || serverLocked());

  async function save() {
    const trimmed = name().trim();
    if (!trimmed) {
      setError(t("ERROR_GENERIC"));
      return;
    }
    setSaving(true);
    setError(null);
    const ex = props.exercise;
    const typeChanged = !!ex && type() !== normalizeType(ex.exerciseType);
    try {
      const saved = ex
        ? await api.updateCatalogExercise(ex.id, {
            name: trimmed,
            muscleGroups: groups(),
            icon: icon(),
            ...(typeChanged && !locked() ? { exerciseType: type() } : {}),
          })
        : await api.createCatalogExercise({
            name: trimmed,
            muscleGroups: groups(),
            exerciseType: type(),
            icon: icon(),
          });
      props.onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && typeChanged) {
        setServerLocked(true);
        setType(normalizeType(ex?.exerciseType));
        setError(t("TYPE_LOCKED"));
      } else {
        setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
      }
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    const ex = props.exercise;
    if (!ex || !(await tgConfirm(t("CONFIRM_DELETE")))) return;
    try {
      await api.deleteCatalogExercise(ex.id);
      props.onDeleted?.(ex.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ERROR_GENERIC"));
    }
  }

  return (
    <BottomSheet
      open={props.open}
      onClose={props.onClose}
      title={editing() ? t("EDIT_EXERCISE") : t("NEW_EXERCISE")}
    >
      <div class="tg-section mb-3">
        <div class="px-4 py-3">
          <label class="label">{t("NAME")}</label>
          <input
            class="input"
            value={name()}
            autocomplete="off"
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </div>
      </div>

      <Show when={props.open && props.exercise?.id}>
        {(id) => (
          <div class="mb-3">
            <ExerciseRecords exerciseId={id()} />
            <ExerciseProgress exerciseId={id()} exerciseType={props.exercise?.exerciseType} />
          </div>
        )}
      </Show>

      <h2 class="tg-section-header">{t("TYPE_SECTION")}</h2>
      <div class="tg-section" role="radiogroup" aria-label={t("TYPE_SECTION")}>
        <For each={[...EXERCISE_TYPES]}>
          {(ty) => (
            <button
              type="button"
              role="radio"
              aria-checked={type() === ty}
              class="type-row"
              disabled={locked()}
              onClick={() => setType(ty)}
            >
              <ExerciseIcon type={ty} />
              <span class="min-w-0 flex-1">
                <span class="block text-[17px] leading-tight">{t(TYPE_TITLE[ty])}</span>
                <span class="block text-[13px] leading-snug text-ink-muted">{t(TYPE_SUB[ty])}</span>
              </span>
              <Show when={type() === ty}>
                <svg class="h-5 w-5 shrink-0 text-[var(--accent)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </Show>
            </button>
          )}
        </For>
      </div>
      <Show when={locked()}>
        <p class="tg-section-footer" role="note">
          {t("TYPE_LOCKED")}
        </p>
      </Show>

      <h2 class="tg-section-header" style={{ "margin-top": "20px" }}>
        {t("ICON")}
      </h2>
      <div class="tg-section">
        <div class="icon-grid" role="group" aria-label={t("ICON")}>
          <For each={[...PICKABLE_ICONS]}>
            {(key) => (
              <button
                type="button"
                class={icon() === key ? "is-active" : ""}
                aria-pressed={icon() === key}
                aria-label={key}
                onClick={() => setIcon(icon() === key ? null : key)}
              >
                <ExerciseIcon icon={key} />
              </button>
            )}
          </For>
        </div>
      </div>

      <div class="tg-section mt-3">
        <div class="px-4 py-3">
          <MuscleGroupPicker value={groups()} onChange={setGroups} />
        </div>
      </div>

      <Show when={error() && error() !== t("TYPE_LOCKED")}>
        <p class="mt-3 px-1 text-[15px] text-[var(--destructive)]" role="alert">
          {error()}
        </p>
      </Show>

      <div class="mt-4 flex gap-2">
        <button type="button" class="btn-secondary flex-1" onClick={props.onClose}>
          {t("CANCEL")}
        </button>
        <button type="button" class="btn-primary flex-1" disabled={saving()} onClick={() => void save()}>
          {saving() ? t("LOADING") : t("SAVE")}
        </button>
      </div>

      <Show when={editing() && props.onDeleted}>
        <div class="tg-section mt-3">
          <button type="button" class="tg-cell is-destructive" onClick={() => void remove()}>
            <span class="tg-cell-body">
              <span class="tg-cell-title">{t("DELETE")}</span>
            </span>
          </button>
        </div>
      </Show>
    </BottomSheet>
  );
}
